
/**
 * Hook for handling the continuation flow after valuation
 * Created: 2025-05-10
 * Updated: 2026-03-16 - Include ref_code in redirectContext for attribution persistence
 * Updated: 2026-07-08 - Fixed broken isLoggedIn check (was truthy function ref).
 *   Now uses real session from useAuth(). Unauthenticated users always get a fresh
 *   redirectAfterAuth='/sell-my-car' + redirectContext written before navigating to
 *   /auth so login resumes on the listing form (not the seller dashboard).
 *   Skips VIN reservation when unauthenticated.
 */

import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCallback, useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { reserveVin } from "@/services/vinReservationService";
import { getRefCode } from "@/utils/tracking/getRefCode";
import { useAuth } from "@/components/auth/UnifiedAuthProvider";

// Essential fields to include in the valuation data
const ESSENTIAL_FIELDS = ['vin', 'make', 'model', 'year', 'mileage', 'transmission', 'reservePrice', 'engineCapacity'];

/**
 * Filters the valuation data to include only essential fields
 */
const getFilteredValuationData = (valuationData: any) => {
  if (!valuationData) return null;
  
  const filtered: Record<string, any> = {};
  
  // Extract only the essential fields
  ESSENTIAL_FIELDS.forEach(field => {
    if (valuationData[field] !== undefined) {
      filtered[field] = valuationData[field];
    }
  });
  
  // Always ensure reservePrice exists (use valuation as fallback)
  if (!filtered.reservePrice && valuationData.valuation) {
    filtered.reservePrice = valuationData.valuation;
  }
  
  return filtered;
};

export function useValuationContinue() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const isLoggedIn = !!session;
  // Track if navigation has been triggered to prevent multiple navigations
  const [hasNavigated, setHasNavigated] = useState(false);
  
  // Store a navigation ID to ensure we can track each navigation attempt
  const navigationIdRef = useRef<string>(Math.random().toString(36).substr(2, 9));
  
  // Create a safety timer to detect navigation issues
  useEffect(() => {
    // This useEffect only exists to detect potential navigation issues
    const safetyTimer = setTimeout(() => {
      console.log("ValuationContinue: Navigation safety check timer triggered", {
        hasNavigated,
        navigationId: navigationIdRef.current
      });
    }, 8000); // Check after 8 seconds
    
    return () => clearTimeout(safetyTimer);
  }, [hasNavigated]);

  const handleContinue = useCallback(async (valuationData: any) => {
    // Generate unique navigation ID for this attempt
    const navigationId = Math.random().toString(36).substr(2, 9);
    navigationIdRef.current = navigationId;
    
    // Guard against multiple navigation attempts
    if (hasNavigated) {
      console.log("ValuationContinue: Navigation already in progress, skipping", {
        navigationId
      });
      return;
    }
    
    // Mark as navigated to prevent loops
    setHasNavigated(true);
    
    // Filter to include only essential data
    const filteredData = getFilteredValuationData(valuationData);
    
    // Debug logging
    console.log("ValuationContinue: Starting navigation with filtered data:", {
      valuationMake: filteredData?.make,
      valuationModel: filteredData?.model,
      valuationYear: filteredData?.year,
      valuationVin: filteredData?.vin,
      valuationMileage: filteredData?.mileage,
      valuationReservePrice: filteredData?.reservePrice,
      isLoggedIn,
      navigationId
    });
    
    try {
      // Store data in localStorage for the form to use
      if (filteredData) {
        try {
          // Clean any previous data
          localStorage.removeItem('valuationData');
          
          // Store the complete valuation data for form population
          localStorage.setItem('valuationData', JSON.stringify(valuationData));
          
          // Also store individual values for redundancy
          localStorage.setItem('tempVIN', filteredData.vin || '');
          localStorage.setItem('tempMileage', filteredData.mileage?.toString() || '');
          localStorage.setItem('tempGearbox', filteredData.transmission || '');
          
          console.log("ValuationContinue: Successfully saved data to localStorage", {
            dataSize: JSON.stringify(valuationData).length,
            navigationId
          });
        } catch (error) {
          console.error("ValuationContinue: Error saving to localStorage:", error);
        }
      }
      
      if (isLoggedIn && filteredData?.vin) {
        // Get user ID from real session (fallback to localStorage for legacy callers)
        const userId = session?.user?.id || localStorage.getItem('userId');

        if (userId) {
          try {
            console.log("ValuationContinue: Creating VIN reservation", {
              vin: filteredData.vin,
              userId,
              navigationId
            });
            
            // Reserve the VIN before navigation
            const reservationResult = await reserveVin(
              filteredData.vin,
              userId,
              valuationData  // Pass complete valuation data for backend processing
            );
            
            if (reservationResult.success && reservationResult.data?.reservationId) {
              // Store the reservation ID in localStorage
              localStorage.setItem('vinReservationId', reservationResult.data.reservationId);
              console.log("ValuationContinue: VIN reservation created successfully", {
                reservationId: reservationResult.data.reservationId,
                navigationId
              });
              
              // Show success toast for reservation
              toast.success("VIN reserved successfully", {
                description: "Your VIN is now reserved for this listing"
              });
            } else {
              console.warn("ValuationContinue: VIN reservation failed", {
                error: reservationResult.error,
                navigationId
              });
              
              toast.error("VIN reservation issue", {
                description: "Unable to reserve this VIN. You may need to try again later."
              });
            }
          } catch (error) {
            console.error("ValuationContinue: Error creating VIN reservation:", error);
          }
        } else {
          console.warn("ValuationContinue: No user ID found, skipping VIN reservation", {
            navigationId
          });
        }
      }

      // Navigate based on auth status - use random navId to prevent state merges
      if (isLoggedIn) {
        console.log("ValuationContinue: User is logged in, navigating to sell-my-car", {
          navigationId
        });
        
        navigate('/sell-my-car', { 
          state: { 
            fromValuation: true,
            valuationData: filteredData,
            timestamp: Date.now(),
            navId: navigationId
          },
          replace: true // Use replace to prevent back button issues
        });
        
        toast.success("Preparing your listing", {
          description: "Setting up your car details"
        });
      } else {
        console.log("ValuationContinue: User is not logged in, navigating to auth page", {
          navigationId
        });
        
        // Always overwrite any stale redirect context so login resumes on /sell-my-car
        localStorage.setItem('redirectAfterAuth', '/sell-my-car');
        localStorage.setItem('redirectContext', JSON.stringify({
          from: 'valuation',
          type: 'continue',
          timestamp: Date.now(),
          requiresFormCompletion: true,
          navigationId,
          ref_code: getRefCode() || null
        }));
        
        toast.info("Please sign in to continue", {
          description: "Create an account or sign in to proceed with listing your vehicle."
        });
        
        navigate('/auth', {
          state: {
            from: 'valuation',
            returnTo: '/sell-my-car',
            valuationData: filteredData,
            timestamp: Date.now(),
            navId: navigationId
          },
          replace: true // Use replace to prevent back button issues
        });
      }
    } catch (error) {
      console.error("ValuationContinue: Navigation error:", error);
      
      // Reset navigation state to allow retry
      setHasNavigated(false);
      
      toast.error("Something went wrong", {
        description: "Please try again"
      });
    }
  }, [navigate, isLoggedIn, hasNavigated, session]);

  return {
    isLoggedIn,
    handleContinue
  };
}
